import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { RefreshCw, Activity, Search, Upload, Download, UserPlus, Trash2, Edit, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const ActivityLogsPage = () => {
  const { api, isAdmin } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('all');

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: page.toString(), limit: '50' });
      if (actionFilter !== 'all') {
        params.append('action', actionFilter);
      }
      const response = await api.get(`/activity-logs?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      console.error('Fetch logs error:', error);
      toast.error('Failed to fetch activity logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, actionFilter]);

  const getActionIcon = (action) => {
    if (action.includes('SEARCH')) return <Search className="w-4 h-4" />;
    if (action.includes('UPLOAD')) return <Upload className="w-4 h-4" />;
    if (action.includes('EXPORT')) return <Download className="w-4 h-4" />;
    if (action.includes('DELETE')) return <Trash2 className="w-4 h-4" />;
    if (action.includes('CREATE') || action.includes('REGISTER')) return <UserPlus className="w-4 h-4" />;
    if (action.includes('UPDATE')) return <Edit className="w-4 h-4" />;
    return <Activity className="w-4 h-4" />;
  };

  const getActionColor = (action) => {
    if (action.includes('DELETE')) return 'text-red-500 bg-red-100 dark:bg-red-900/30';
    if (action.includes('CREATE') || action.includes('UPLOAD') || action.includes('REGISTER')) return 'text-green-500 bg-green-100 dark:bg-green-900/30';
    if (action.includes('SEARCH')) return 'text-blue-500 bg-blue-100 dark:bg-blue-900/30';
    if (action.includes('EXPORT')) return 'text-amber-500 bg-amber-100 dark:bg-amber-900/30';
    return 'text-muted-foreground bg-muted';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const actionOptions = [
    { value: 'all', label: 'All Actions' },
    { value: 'CUSTOMER_SEARCH', label: 'Customer Search' },
    { value: 'CUSTOMERS_UPLOADED', label: 'Customers Uploaded' },
    { value: 'CUSTOMERS_EXPORTED', label: 'Customers Exported' },
    { value: 'CUSTOMER_CREATED', label: 'Customer Created' },
    { value: 'CUSTOMER_UPDATED', label: 'Customer Updated' },
    { value: 'CUSTOMER_SOFT_DELETED', label: 'Customer Deleted' },
    { value: 'USER_LOGIN', label: 'User Login' },
    { value: 'USER_CREATED', label: 'User Created' },
  ];

  return (
    <div className="space-y-6" data-testid="activity-logs-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">Activity Logs</h1>
          <p className="text-muted-foreground mt-1">
            View system activity and user actions
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={actionFilter} onValueChange={(value) => { setActionFilter(value); setPage(1); }}>
            <SelectTrigger className="w-[200px]" data-testid="action-filter">
              <SelectValue placeholder="Filter by action" />
            </SelectTrigger>
            <SelectContent>
              {actionOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchLogs} data-testid="refresh-logs-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Logs List */}
      <Card className="shadow-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-primary" />
            </div>
          ) : logs.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              No activity logs found
            </div>
          ) : (
            <div className="divide-y divide-border">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="p-4 hover:bg-muted/50 transition-colors"
                  data-testid={`log-${log.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getActionColor(log.action)}`}>
                      {getActionIcon(log.action)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-foreground">{log.user_email}</span>
                        <span className="text-muted-foreground">•</span>
                        <span className="text-sm text-muted-foreground">
                          {formatTimestamp(log.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm mt-1">
                        <span className="font-medium text-primary">{log.action.replace(/_/g, ' ')}</span>
                        {log.details && (
                          <span className="text-muted-foreground ml-2">— {log.details}</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1}
          data-testid="logs-prev-page"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </Button>
        <span className="text-sm text-muted-foreground">Page {page}</span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(p => p + 1)}
          disabled={logs.length < 50}
          data-testid="logs-next-page"
        >
          Next
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

export default ActivityLogsPage;
